import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';

import type { State } from '@avalara/types/reducer-states';
import PageNotFound from '../../components/page-not-found';
import ContactDialog from '../../components/product-catalog/contact-dialog';
import OrderInProgressDialog from '../../components/product-catalog/order-in-progress-dialog';
import productCatalogData from '../../components/product-catalog/product-catalog-data.json';
import ProductDetailPage from '../../components/product-catalog/product-detail';
import analytics from '../../lib/analytics';
import { fetchProductDetail } from '../../lib/side-effects/product-detail';
import { useChiliPiperContact } from './use-chilipiper-contact';
import { useProductCatalogShell } from './use-product-catalog-shell';

import type { ProductCatalogItem } from '../../../../types/product-catalog';

const products = productCatalogData.products as Array<ProductCatalogItem>;

const ProductDetail = () => {
  const { productDetail: productKey } = useParams<{ productDetail: string }>();
  const {
    dispatch,
    accountId,
    cam,
    camOwned,
    customerAccountId,
    obligations,
    user,
    isLoading,
    nextBestProduct,
    renderNavigation,
    shouldRender,
  } = useProductCatalogShell();
  const productDetail = useSelector((state: State) => state.productDetail);

  const {
    contactDialogProduct,
    schedulingInProgress,
    contactError,
    handleContactClick: handleChiliPiperContactClick,
    handleDismissDialog,
  } = useChiliPiperContact(shouldRender, customerAccountId, user, cam, camOwned);
  const [orderInProgressOpen, setOrderInProgressOpen] = useState(false);

  useEffect(() => {
    if (!shouldRender || !productKey) return;

    dispatch(fetchProductDetail(productKey));
  }, [shouldRender, productKey, dispatch]);

  const handleContactClick = (item: ProductCatalogItem) => {
    analytics.track(`Product detail recommendation contact clicked: ${item.slug}`);
    handleChiliPiperContactClick(item);
  };

  if (!shouldRender) return null;

  const item = products.find((product) => product.slug === productKey);

  if (!item || productDetail.fetchStatus === 'NOT_FOUND') return <PageNotFound />;

  return (
    <>
      {renderNavigation()}
      {accountId && !isLoading && productDetail.fetchStatus === 'SUCCESS' && (
        <>
          <ProductDetailPage
            item={item}
            content={productDetail}
            obligations={obligations}
            recommendedProductIds={nextBestProduct.productIds}
            onContactClick={handleContactClick}
            onOrderInProgress={() => setOrderInProgressOpen(true)}
          />
          <ContactDialog
            productTitle={contactDialogProduct?.title ?? ''}
            dialogOpen={Boolean(contactDialogProduct)}
            schedulingInProgress={schedulingInProgress}
            hasError={contactError}
            handleDismissDialog={handleDismissDialog}
          />
        </>
      )}
      {/* Deliberately NOT gated on `!isLoading` alongside the block above: isLoading is a global
          "some request is in flight somewhere in the app" flag (src/client/store/data.ts, bumped
          by every call through lib/fetch.ts — notification polling, nav refresh, anything), not
          something scoped to this page. Gating this dialog on it would unmount it the instant any
          unrelated background request fires while it's open, silently closing it under the
          customer. Its own visibility is independently controlled by dialogOpen below. */}
      <OrderInProgressDialog
        dialogOpen={orderInProgressOpen}
        handleDismissDialog={() => setOrderInProgressOpen(false)}
      />
    </>
  );
};

export default ProductDetail;
